import { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { X, Camera, Loader2 } from "lucide-react";
import { Html5Qrcode } from "html5-qrcode";

interface QRScannerProps {
  onResult: (result: string) => void;
  onClose: () => void;
}

export default function QRScanner({ onResult, onClose }: QRScannerProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const scannerRef = useRef<Html5Qrcode | null>(null);
  const isRunningRef = useRef(false);
  const onResultRef = useRef(onResult);
  const [error, setError] = useState<string | null>(null);
  const [starting, setStarting] = useState(true);

  useEffect(() => {
    onResultRef.current = onResult;
  });

  useEffect(() => {
    const id = "qr-scanner-container";
    const scanner = new Html5Qrcode(id);
    scannerRef.current = scanner;

    scanner.start(
      { facingMode: "environment" },
      { fps: 10, qrbox: { width: 250, height: 250 } },
      (decodedText) => {
        if (isRunningRef.current) {
          isRunningRef.current = false;
          scanner.stop().catch(() => {});
        }
        onResultRef.current(decodedText);
      },
      () => {}
    )
      .then(() => {
        isRunningRef.current = true;
        setStarting(false);
      })
      .catch((err: any) => {
        setError("Camera access denied. Please allow camera permissions.");
        setStarting(false);
        console.error("QR scanner error:", err);
      });

    return () => {
      if (isRunningRef.current) {
        isRunningRef.current = false;
        scanner.stop().catch(() => {});
      }
    };
  }, []);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80">
      <div className="bg-background rounded-xl w-full max-w-sm mx-4 overflow-hidden shadow-2xl">
        <div className="flex items-center justify-between px-4 py-3 border-b">
          <div className="flex items-center gap-2">
            <Camera className="h-4 w-4" />
            <span className="font-medium text-sm">Scan QR Code</span>
          </div>
          <Button variant="ghost" size="icon" onClick={onClose} data-testid="button-close-scanner">
            <X className="h-4 w-4" />
          </Button>
        </div>
        <div className="p-4">
          {error ? (
            <div className="text-center py-8 text-destructive text-sm">{error}</div>
          ) : (
            <>
              {starting && (
                <div className="flex items-center justify-center py-8 gap-2 text-muted-foreground">
                  <Loader2 className="h-5 w-5 animate-spin" />
                  <span className="text-sm">Starting camera...</span>
                </div>
              )}
              <div id="qr-scanner-container" ref={containerRef} className="w-full rounded-lg overflow-hidden" data-testid="div-qr-scanner" />
            </>
          )}
          <p className="text-xs text-muted-foreground text-center mt-3">
            Point camera at a ticket QR code
          </p>
        </div>
      </div>
    </div>
  );
}
